// Canonical reference of every {variableName} usable in the DB-driven email/SMS template
// system (ReminderTemplate → renderDynamicEmail/renderEmailTemplate). Grouped by
// reminderType, matching the admin dashboard's "Reminder Type" field, so the variable
// picker can show only what's actually relevant to the template being edited.
//
// Sourced from an audit of every seeded template (src/seed/seedEmailTemplates.js) and
// every call site that passes a `variables` object into renderDynamicEmail/renderEmailTemplate.
//
// NOT included here: the separate {{double-brace}} payment HTML template system
// (src/templates/email/payment/*.html + paymentEmailService.js) — it's a different,
// incompatible placeholder syntax and must not be mixed into this list.
//
// Keep this in sync manually when a template's variables object changes shape.

export const EMAIL_TEMPLATE_VARIABLES = {
  account: {
    label: 'User & Account',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'lastName', description: "User's last name"},
      {key: 'email', description: "User's email address"},
      {key: 'username', description: "User's username"},
      {
        key: 'verificationLinkWithToken',
        description:
          'Email verification link (expires in 30 minutes) — email-verification template only',
      },
    ],
  },

  renewal: {
    label: 'Subscription Renewal',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'planName', description: 'Name of the subscription plan'},
      {key: 'planDescription', description: 'Short description of the plan'},
      {key: 'nextDueDate', description: 'Date the renewal payment is due'},
      {
        key: 'nextPaymentAmount',
        description: 'Amount that will be charged on renewal',
      },
      {
        key: 'nextBillingDate',
        description: 'Next billing date after this renewal',
      },
      {key: 'currency', description: 'Currency code, e.g. USD, INR'},
      {key: 'subscriptionId', description: 'Subscription identifier'},
      {
        key: 'subscriptionName',
        description: 'Display name of the subscription',
      },
      {key: 'subscriptionNumber', description: 'Subscription number/reference'},
      {key: 'amount', description: 'Amount charged for this renewal'},
      {
        key: 'addOns',
        description: 'Array of add-ons included — use with an {{#each}} block',
      },
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  due: {
    label: 'Due Date',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'planName', description: 'Name of the subscription plan'},
      {key: 'dueDate', description: 'Date the payment is due'},
      {key: 'amount', description: 'Amount due'},
      {key: 'currency', description: 'Currency code'},
      {key: 'paymentLink', description: 'Link to complete payment'},
    ],
  },

  overdue: {
    label: 'Overdue',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  cart: {
    label: 'Abandoned Cart',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {
        key: 'itemsList',
        description: 'Formatted list of items left in the cart',
      },
      {key: 'itemCount', description: 'Number of items in the cart'},
      {key: 'cartTotal', description: 'Total value of the cart'},
      {key: 'currency', description: 'Currency code'},
      {key: 'checkoutLink', description: 'Link back to checkout'},
    ],
  },

  order: {
    label: 'Order',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'orderId', description: 'Order identifier'},
      {
        key: 'orderNumber',
        description:
          'Order number — used by a webhook path instead of orderId; keep both available until reconciled',
      },
      {key: 'orderDate', description: 'Date the order was placed'},
      {key: 'amount', description: 'Total order amount'},
      {key: 'currency', description: 'Currency code'},
      {key: 'paymentMethod', description: 'Payment method used'},
      {key: 'paymentStatus', description: 'Payment status of the order'},
      {key: 'subtotal', description: 'Order subtotal before tax/discounts'},
      {key: 'tax', description: 'Tax amount'},
      {key: 'discountedTotal', description: 'Total after discounts applied'},
      {key: 'totalItems', description: 'Number of items in the order'},
      {
        key: 'totalSubscriptions',
        description: 'Number of subscription items in the order',
      },
      {
        key: 'isSubscription',
        description: 'Whether the order contains a subscription (true/false)',
      },
      {key: 'itemName', description: 'Name of the purchased item'},
      {key: 'itemType', description: 'Type of the purchased item'},
      {
        key: 'orderTotal',
        description: "This item's total (per-item confirmation emails)",
      },
      {key: 'orderTax', description: "This item's tax amount"},
      {key: 'orderSubtotal', description: "This item's subtotal"},
      {key: 'supportEmail', description: 'Support contact email'},

      {key: 'itemNumber', description: 'Item number/reference'},
      {key: 'basePrice', description: 'Base price before any discounts'},
      {key: 'finalPrice', description: 'Final price after all discounts'},
      {key: 'monthlyPrice', description: 'Monthly recurring price'},
      {key: 'taxRate', description: 'Tax rate percentage applied'},
      {key: 'durationMonths', description: 'Subscription duration in months'},
      {key: 'subscriptionNumber', description: 'Subscription number/reference'},
      {key: 'subscriptionRenewalDate', description: 'Next renewal date'},
      {key: 'currencyCode', description: 'ISO currency code'},
      {key: 'currencySymbol', description: 'Currency symbol, e.g. $, ₹'},
      {key: 'myOrdersUrl', description: "Link to the user's orders page"},
    ],
  },

  subscription: {
    label: 'Subscription',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {
        key: 'orderId',
        description: 'Order identifier this subscription was purchased in',
      },
      {key: 'orderDate', description: 'Date the order was placed'},
      {key: 'itemName', description: 'Name of the subscription plan/item'},
      {key: 'itemType', description: 'Type of the item'},
      {key: 'subscriptionNumber', description: 'Subscription number/reference'},
      {key: 'subscriptionStatus', description: 'Current subscription status'},
      {key: 'subscriptionStartDate', description: 'Subscription start date'},
      {key: 'subscriptionRenewalDate', description: 'Next renewal date'},
      {key: 'subscriptionCycles', description: 'Current billing cycle number'},
      {
        key: 'subscriptionCyclesTotal',
        description: 'Total billing cycles (if fixed-term)',
      },
      {
        key: 'autoRenewEnabled',
        description: 'Whether auto-renew is enabled (true/false)',
      },
      {key: 'durationMonths', description: 'Subscription duration in months'},
      {key: 'currency', description: 'Currency code'},
      {key: 'currencyCode', description: 'ISO currency code'},
      {key: 'currencySymbol', description: 'Currency symbol, e.g. $, ₹'},
      {key: 'paymentMethod', description: 'Payment method used'},
      {key: 'orderTotal', description: 'Order total'},
      {key: 'orderTax', description: 'Order tax amount'},
      {key: 'orderSubtotal', description: 'Order subtotal'},
      {key: 'supportEmail', description: 'Support contact email'},

      {key: 'nextDueDate', description: 'Next due date for payment'},
    ],
  },

  suspension: {
    label: 'Suspension',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  payment: {
    label: 'Payment (Order)',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'orderId', description: 'Order identifier'},
      {key: 'amountDue', description: 'Amount still owed'},
      {key: 'amount', description: 'Payment amount'},
      {key: 'currency', description: 'Currency code'},
      {key: 'paymentLink', description: 'Link to complete payment'},
      {key: 'createdDate', description: 'Date the order/payment was created'},
      {key: 'subtotal', description: 'Order subtotal'},
      {key: 'tax', description: 'Tax amount'},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  support: {
    label: 'Support / Contact',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'ticketId', description: 'Support ticket ID'},
      {key: 'ticketSubject', description: 'Subject line of the ticket'},
      {key: 'status', description: 'Current ticket status'},
      {key: 'ticketUrl', description: 'Link to view the ticket'},
      {key: 'message', description: 'The reply/message content'},
      {
        key: 'adminName',
        description: "Replying admin's name (admin-facing notifications)",
      },
      {
        key: 'userName',
        description: "Ticket owner's name (admin-facing notifications)",
      },
      {
        key: 'userEmail',
        description: "Ticket owner's email (admin-facing notifications)",
      },
      {key: 'inquiryId', description: 'Contact form inquiry ID'},
    ],
  },

  payment_failed: {
    label: 'Payment Failed',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'orderId', description: 'Order identifier'},
      {key: 'amount', description: 'Amount that failed to charge'},
      {key: 'currency', description: 'Currency code'},
      {key: 'failureReason', description: 'Reason the payment failed'},
      {
        key: 'subscriptionName',
        description: 'Name of the affected subscription',
      },
      {key: 'amountDue', description: 'Amount still owed'},
      {key: 'dueDate', description: 'Original due date'},
      {
        key: 'daysLeftInGrace',
        description: 'Days remaining in the grace period',
      },
      {
        key: 'daysLeftTotal',
        description: 'Days remaining in the overall resolution window',
      },
      {
        key: 'inGracePeriod',
        description:
          'Whether the subscription is currently in its grace period (true/false)',
      },
      {
        key: 'inResolutionWindow',
        description:
          'Whether past grace and in the final resolution window (true/false)',
      },
      {
        key: 'legalActionWarning',
        description: 'Whether to show a legal-action warning (true/false)',
      },
      {
        key: 'paymentLink',
        description:
          'Link to update payment / retry — currently unimplemented server-side, will render empty',
      },
      {key: 'invoiceUrl', description: 'Link to view the invoice'},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  payment_cancelled: {
    label: 'Payment Cancelled',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {
        key: 'orderId',
        description: 'Order identifier (used by most call sites)',
      },
      {
        key: 'orderNumber',
        description:
          'Order number — used instead of orderId by the Stripe webhook path; template body currently references {orderId}, so keep both available until reconciled',
      },
      {key: 'amount', description: 'Cancelled payment amount'},
      {key: 'currency', description: 'Currency code'},
      {key: 'cancellationReason', description: 'Reason for cancellation'},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  payment_recovered: {
    label: 'Payment Recovered',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {
        key: 'subscriptionName',
        description: 'Name of the recovered subscription',
      },
      {key: 'subscriptionNumber', description: 'Subscription number/reference'},
      {key: 'amount', description: 'Amount successfully charged'},
      {key: 'currency', description: 'Currency code'},
      {key: 'paymentDate', description: 'Date the payment succeeded'},
      {key: 'nextBillingDate', description: 'Next billing date'},
      {
        key: 'daysOverdue',
        description: 'How many days the payment had been overdue',
      },
      {
        key: 'myOrdersUrl',
        description: "Link to the user's orders/subscriptions page",
      },
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  payment_overdue: {
    label: 'Payment Overdue',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'planName', description: 'Name of the subscription plan'},
      {key: 'daysOverdue', description: 'Number of days overdue'},
      {key: 'overdueAmount', description: 'Amount overdue'},
      {key: 'amountDue', description: 'Amount due'},
      {key: 'currency', description: 'Currency code'},
      {key: 'paymentLink', description: 'Link to complete payment'},
      {key: 'nextPaymentDate', description: "Next attempt's scheduled date"},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  refund: {
    label: 'Refund',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'orderId', description: 'Order identifier'},
      {key: 'refundAmount', description: 'Amount refunded'},
      {key: 'currency', description: 'Currency code'},
      {key: 'refundDate', description: 'Date the refund was processed'},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  trial: {
    label: 'Trial',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'planName', description: 'Name of the trialled plan'},
      {key: 'trialEndDate', description: 'Date the trial ends'},
      {key: 'amount', description: 'Amount to be charged after the trial'},
      {key: 'currency', description: 'Currency code'},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  invoice: {
    label: 'Invoice',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },

  design: {
    label: 'Design Purchase',
    variables: [
      {key: 'firstName', description: "User's first name"},
      {key: 'orderId', description: 'Order identifier'},
      {key: 'orderDate', description: 'Date the order was placed'},
      {key: 'designTitle', description: 'Title of the purchased design'},
      {key: 'licensingType', description: 'License type purchased'},
      {key: 'formats', description: 'File formats included'},
      {key: 'finalAmount', description: 'Final amount paid'},
      {key: 'originalPrice', description: 'Original price before any discount'},
      {key: 'currencyCode', description: 'ISO currency code'},
      {key: 'currencySymbol', description: 'Currency symbol'},
      {key: 'myOrdersUrl', description: "Link to the user's orders page"},
      {key: 'supportEmail', description: 'Support contact email'},
    ],
  },
};

// Handlebars helper functions auto-injected into every template rendered via
// renderDynamicEmail (NOT available in templates rendered directly via renderEmailTemplate,
// e.g. the welcome email — see src/utils/renderDynamicEmail.js).
export const EMAIL_TEMPLATE_HELPERS = [
  {
    key: 'formatPrice',
    description: 'Formats a price value. Usage: {{formatPrice amount}}',
  },
  {
    key: 'formatCurrency',
    description:
      'Formats amount + currency together. Usage: {{formatCurrency amount currency}}',
  },
  {
    key: 'safeFormatDate',
    description:
      "Formats a date safely, returns 'N/A' if invalid. Usage: {{safeFormatDate date}}",
  },
];

// Footer-only variables auto-injected by renderEmailTemplate for every template
// (the shared emailBase.mjml layout's Terms/Privacy/Unsubscribe links).
export const EMAIL_FOOTER_VARIABLES = [
  {key: 'siteUrl', description: 'Site base URL (auto-injected)'},
  {key: 'termsUrl', description: 'Terms page URL (auto-injected)'},
  {key: 'privacyUrl', description: 'Privacy page URL (auto-injected)'},
  {
    key: 'unsubscribeUrl',
    description:
      'Unsubscribe link (auto-injected — requires an `email` variable to be passed in)',
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// SCHEMA_REFERENCE — every customer-relevant field from every database model that
// could plausibly appear in a template, organized strictly by table/model (not by
// event), per an audit of every model file in src/models/.
//
// `wired: true`  → confirmed today: some real call site already passes this exact
//                  value into a template's variables object (cross-referenced
//                  against EMAIL_TEMPLATE_VARIABLES above and the actual controllers).
// `wired: false` → the field genuinely exists in the database, but no controller
//                  currently copies it into any template's variables object.
//                  Picking one of these WILL render as an empty string in the sent
//                  email/SMS until a developer wires it into the relevant call site
//                  (Handlebars silently renders missing variables as blank, not as
//                  a visible placeholder — so this fails silently, not loudly).
//
// Deliberately EXCLUDED from every model below (never appropriate in a customer
// template, regardless of `wired` status): passwords/hashes, refresh/access tokens,
// encryption keys/IVs, raw internal IDs that only make sense as foreign keys
// (e.g. countryId, durationId, subscriptionModuleConfigId), and large nested
// JSON/snapshot blobs (itemSnapshot, metadata, featuresConfiguration, etc.) that
// aren't simple scalar values Handlebars can substitute directly.
// ═══════════════════════════════════════════════════════════════════════════════

export const SCHEMA_REFERENCE = {
  User: {
    table: 'users',
    fields: [
      {key: 'firstName', description: "User's first name", wired: true},
      {key: 'lastName', description: "User's last name", wired: true},
      {key: 'email', description: "User's email address", wired: true},
      {key: 'username', description: "User's username", wired: true},
      {key: 'phoneNumber', description: "User's phone number", wired: false},
      {
        key: 'avatar',
        description: "URL of the user's avatar image",
        wired: false,
      },
      {
        key: 'isVerified',
        description: 'Whether the email is verified (true/false)',
        wired: false,
      },
      {key: 'state', description: "User's state/province", wired: false},
      {key: 'city', description: "User's city", wired: false},
      {key: 'streetName', description: "User's street name", wired: false},
      {
        key: 'houseNumber',
        description: "User's house/unit number",
        wired: false,
      },
      {key: 'postalCode', description: "User's postal code", wired: false},
      {
        key: 'status',
        description: 'Account status: active or inactive',
        wired: false,
      },
      {key: 'timeZone', description: "User's timezone", wired: false},
    ],
  },

  Country: {
    table: 'countries',
    fields: [
      {key: 'countryName', description: 'Full country name', wired: false},
      {
        key: 'displayName',
        description: 'Display-friendly country name',
        wired: false,
      },
      {key: 'countryCode', description: 'ISO country code', wired: false},
      {
        key: 'currencyCode',
        description: 'ISO currency code for this country',
        wired: true,
      },
      {key: 'currencyName', description: 'Full currency name', wired: false},
      {
        key: 'currencySymbol',
        description: 'Currency symbol, e.g. $, ₹',
        wired: true,
      },
      {
        key: 'timezone',
        description: "Country's default timezone",
        wired: false,
      },
      {
        key: 'phoneCode',
        description: 'International dialing code',
        wired: false,
      },
      {
        key: 'flagEmoji',
        description: 'Flag emoji for the country',
        wired: false,
      },
    ],
  },

  BusinessDetail: {
    table: 'business_details',
    fields: [
      {key: 'businessName', description: "User's business name", wired: false},
      {
        key: 'businessCategory',
        description: 'Category of the business',
        wired: false,
      },
      {
        key: 'businessContactNumber',
        description: 'Business contact phone number',
        wired: false,
      },
      {key: 'websiteURL', description: "Business's website URL", wired: false},
      {
        key: 'businessStreetName',
        description: 'Business street name',
        wired: false,
      },
      {
        key: 'businessHouseNumber',
        description: 'Business house/unit number',
        wired: false,
      },
      {
        key: 'businessPostalCode',
        description: 'Business postal code',
        wired: false,
      },
      {key: 'businessCity', description: 'Business city', wired: false},
      {
        key: 'businessState',
        description: 'Business state/province',
        wired: false,
      },
      {key: 'businessCountry', description: 'Business country', wired: false},
      {
        key: 'taxNumber',
        description: "Business's tax/GST number",
        wired: false,
      },
    ],
  },

  Order: {
    table: 'orders',
    fields: [
      {key: 'orderId', description: 'Order identifier', wired: true},
      {
        key: 'orderNumber',
        description:
          'Human-readable order number, e.g. DES-ORD-12345 — used by a webhook path instead of orderId in some flows',
        wired: true,
      },
      {key: 'orderDate', description: 'Date the order was placed', wired: true},
      {
        key: 'oneTimeTotal',
        description: 'Total for one-time (non-subscription) items',
        wired: false,
      },
      {
        key: 'recurringMonthlyTotal',
        description: 'Monthly total for recurring subscription items',
        wired: false,
      },
      {
        key: 'subtotal',
        description: 'Order subtotal before tax/discounts',
        wired: true,
      },
      {
        key: 'currency',
        description: 'Currency the order was placed in',
        wired: true,
      },
      {
        key: 'orderStatus',
        description:
          'Order status: pending, completed, failed, on_hold, in_process, cancelled, expired',
        wired: false,
      },
      {
        key: 'promoDiscount',
        description: 'Discount applied via a promo code, if any',
        wired: false,
      },
      {
        key: 'paymentStatus',
        description: 'Current payment status',
        wired: true,
      },
      {
        key: 'discountedTotal',
        description: 'Total after discounts applied',
        wired: true,
      },
      {
        key: 'amount',
        description: 'Final total amount (after tax)',
        wired: true,
      },
      {key: 'tax', description: 'Tax amount', wired: true},
      {
        key: 'orderType',
        description: 'mixed, products_only, valuepacks_only, or plan_only',
        wired: false,
      },
      {
        key: 'totalItems',
        description: 'Number of items in the order',
        wired: true,
      },
      {
        key: 'totalSubscriptions',
        description: 'Number of subscription items in the order',
        wired: true,
      },
      {
        key: 'isSubscription',
        description: 'Whether the order contains a subscription (true/false)',
        wired: true,
      },
      {key: 'paymentMethod', description: 'Payment method used', wired: true},
      {
        key: 'myOrdersUrl',
        description: "Link to the user's orders page",
        wired: false,
      },
    ],
  },

  OrderItem: {
    table: 'order_items',
    fields: [
      {
        key: 'itemType',
        description: 'plan, package, valuepack, product, or design',
        wired: true,
      },
      {key: 'billingType', description: 'recurring or onetime', wired: false},
      {
        key: 'basePrice',
        description: 'Base price before any discounts',
        wired: false,
      },
      {
        key: 'itemDiscount',
        description: 'Item-specific discount amount',
        wired: false,
      },
      {
        key: 'finalPriceAfterDiscount',
        description: 'Final price after all discounts',
        wired: false,
      },
      {
        key: 'pricePerMonth',
        description: 'Monthly recurring price',
        wired: false,
      },
      {
        key: 'totalPrice',
        description: 'Total price for the selected duration',
        wired: false,
      },
      {key: 'quantity', description: 'Quantity purchased', wired: false},
      {
        key: 'itemName',
        description: "The item's name (from its snapshot)",
        wired: true,
      },
      {
        key: 'orderTotal',
        description: "This item's total (per-item confirmation emails)",
        wired: true,
      },
      {key: 'orderTax', description: "This item's tax amount", wired: true},
      {key: 'orderSubtotal', description: "This item's subtotal", wired: true},
      {key: 'itemNumber', description: 'Item number/reference', wired: false},
    ],
  },

  Subscription: {
    table: 'subscriptions',
    fields: [
      {
        key: 'subscriptionId',
        description: 'Subscription identifier',
        wired: true,
      },
      {
        key: 'subscriptionNumber',
        description: 'Human-readable subscription number, e.g. DES-SUB-12345',
        wired: true,
      },
      {
        key: 'subscriptionName',
        description: 'Display name of the subscription',
        wired: true,
      },
      {
        key: 'subscriptionType',
        description:
          'subscription_no_cancellation or subscription_cancellation_allowed',
        wired: false,
      },
      {
        key: 'taxRate',
        description: 'Tax rate percentage applied',
        wired: false,
      },
      {key: 'taxAmount', description: 'Tax amount per month', wired: false},
      {
        key: 'monthlyAmountWithTax',
        description: 'Monthly amount including tax',
        wired: false,
      },
      {
        key: 'totalAmountWithTax',
        description: 'Total amount including tax for the full duration',
        wired: false,
      },
      {
        key: 'stripeSubscriptionId',
        description: 'Stripe subscription ID',
        wired: false,
      },
      {key: 'startDate', description: 'Subscription start date', wired: true},
      {
        key: 'endDate',
        description: 'Subscription end date (when it will be cancelled)',
        wired: true,
      },
      {
        key: 'currentPeriodStart',
        description: 'Current billing period start',
        wired: false,
      },
      {
        key: 'currentPeriodEnd',
        description: 'Current billing period end',
        wired: false,
      },
      {key: 'renewalDate', description: 'Next billing date', wired: true},
      {
        key: 'canceledAt',
        description: 'Date the subscription was cancelled',
        wired: false,
      },
      {
        key: 'status',
        description:
          'active, past_due, canceled, expired, incomplete, trialing, unpaid, paused',
        wired: true,
      },
      {
        key: 'paymentStatus',
        description:
          'paid, pending, failed, past_due, grace_period, resolution_window, cancelled_non_payment, cancelled_breach',
        wired: false,
      },
      {
        key: 'gracePeriodStart',
        description: 'Start of the grace period after a payment failure',
        wired: false,
      },
      {
        key: 'gracePeriodEnd',
        description: 'End of the grace period',
        wired: false,
      },
      {
        key: 'resolutionWindowEnd',
        description: 'End of the resolution window',
        wired: false,
      },
      {
        key: 'failedPaymentAttempts',
        description: 'Number of failed payment attempts',
        wired: false,
      },
      {
        key: 'paymentDueDate',
        description: 'Date by which payment must be made',
        wired: false,
      },
      {
        key: 'nextRetryDate',
        description: 'Next scheduled payment retry date',
        wired: false,
      },
      {
        key: 'totalAmount',
        description: 'Monthly subscription amount (excluding tax)',
        wired: false,
      },
      {key: 'currency', description: 'Currency code', wired: true},
      {key: 'currencyCode', description: 'ISO currency code', wired: true},
      {key: 'currencySymbol', description: 'Currency symbol', wired: true},
      {
        key: 'totalCycles',
        description: 'Total billing cycles before auto-cancel',
        wired: true,
      },
      {
        key: 'currentCycle',
        description: 'Current billing cycle number',
        wired: true,
      },
      {
        key: 'autoRenewEnabled',
        description: 'Whether auto-renewal is enabled (true/false)',
        wired: true,
      },
      {
        key: 'cancelAtPeriodEnd',
        description:
          'Whether the subscription will cancel at period end (true/false)',
        wired: false,
      },
      {
        key: 'durationMonths',
        description: 'Subscription duration in months',
        wired: true,
      },
      {
        key: 'daysRemaining',
        description: 'Days remaining in the current period (computed)',
        wired: false,
      },
      {
        key: 'paymentLifecycleStage',
        description:
          "Human-readable stage, e.g. 'Active', 'Past Due', 'Grace Period' (computed)",
        wired: false,
      },
    ],
  },

  Cart: {
    table: 'carts',
    fields: [
      {key: 'cartTotal', description: 'Total value of the cart', wired: true},
      {
        key: 'itemCount',
        description: 'Number of items in the cart',
        wired: true,
      },
      {
        key: 'itemsList',
        description: 'Formatted list of items in the cart',
        wired: true,
      },
      {
        key: 'subtotal',
        description: 'Cart subtotal before discounts/tax',
        wired: false,
      },
      {
        key: 'discountAmount',
        description: 'Discount applied to the cart',
        wired: false,
      },
      {
        key: 'discountedTotal',
        description: 'Cart total after discount',
        wired: false,
      },
      {key: 'tax', description: 'Tax amount on the cart', wired: false},
      {
        key: 'finalTotal',
        description: 'Final cart total after tax',
        wired: false,
      },
      {
        key: 'status',
        description: 'active, processing, completed, or abandoned',
        wired: false,
      },
    ],
  },

  PaymentMethod: {
    table: 'payment_methods',
    fields: [
      {key: 'paymentType', description: 'card, upi, or bank', wired: false},
      {
        key: 'paymentDetails',
        description:
          'Non-sensitive payment info such as last 4 digits or UPI ID — never expose full card/bank details',
        wired: false,
      },
    ],
  },

  SupportTicket: {
    table: 'support_tickets',
    fields: [
      {key: 'ticketId', description: 'Support ticket ID', wired: true},
      {
        key: 'ticketSubject',
        description: 'Subject line of the ticket (DB field: subject)',
        wired: true,
      },
      {
        key: 'status',
        description: 'open, in_progress, pending, resolved, or closed',
        wired: true,
      },
      {key: 'ticketUrl', description: 'Link to view the ticket', wired: true},
      {
        key: 'ticketType',
        description:
          'Technical Issue, Billing & Payments, General Inquiry, Feature Request, or Service',
        wired: false,
      },
      {key: 'priority', description: 'low, medium, or high', wired: false},
      {
        key: 'department',
        description: 'Department the ticket is assigned to',
        wired: false,
      },
      {key: 'message', description: 'The reply/message content', wired: true},
      {
        key: 'adminName',
        description: "Replying admin's name (admin-facing notifications only)",
        wired: true,
      },
      {
        key: 'userName',
        description: "Ticket owner's name (admin-facing notifications only)",
        wired: true,
      },
      {
        key: 'userEmail',
        description: "Ticket owner's email (admin-facing notifications only)",
        wired: true,
      },
    ],
  },

  DesignItem: {
    table: 'design_items',
    fields: [
      {
        key: 'designTitle',
        description: 'Title of the design (DB field: title)',
        wired: true,
      },
      {
        key: 'shortDescription',
        description: 'Short description of the design',
        wired: false,
      },
      {key: 'formats', description: 'File formats included', wired: true},
      {
        key: 'licensingType',
        description: 'standard, commercial, or exclusive',
        wired: true,
      },
      {key: 'basePrice', description: 'Base price of the design', wired: false},
    ],
  },

  DesignOrder: {
    table: 'design_orders',
    fields: [
      {
        key: 'licensingType',
        description: 'standard, commercial, or exclusive',
        wired: true,
      },
      {
        key: 'originalPrice',
        description: 'Price before any discount',
        wired: true,
      },
      {
        key: 'finalAmount',
        description: 'Final amount actually paid',
        wired: true,
      },
      {key: 'currency', description: 'Currency code', wired: true},
      {
        key: 'paymentStatus',
        description: 'pending, completed, failed, or refunded',
        wired: false,
      },
      {
        key: 'orderStatus',
        description: 'pending, completed, cancelled, or refunded',
        wired: false,
      },
      {
        key: 'downloadCount',
        description: 'Number of times the files have been downloaded',
        wired: false,
      },
    ],
  },

  ValuePack: {
    table: 'value_packs',
    fields: [
      {key: 'name', description: 'ValuePack name', wired: false},
      {key: 'subTitle', description: 'ValuePack subtitle', wired: false},
      {
        key: 'shortDescription',
        description: 'Short description of the ValuePack',
        wired: false,
      },
      {key: 'slug', description: 'URL slug for the ValuePack', wired: false},
    ],
  },

  ValuePackTier: {
    table: 'value_pack_tiers',
    fields: [
      {key: 'tierType', description: 'The tier type/level', wired: false},
      {key: 'name', description: 'Tier name', wired: false},
      {key: 'description', description: 'Tier description', wired: false},
      {
        key: 'originalPrice',
        description: 'Original/MRP price before discount',
        wired: false,
      },
      {
        key: 'pricePerMonth',
        description: 'Calculated price per month',
        wired: false,
      },
      {
        key: 'currentPrice',
        description: 'Actual selling price after discount',
        wired: false,
      },
      {key: 'discountType', description: 'percentage or flat', wired: false},
      {
        key: 'discountValue',
        description: 'Discount amount (percentage or flat)',
        wired: false,
      },
    ],
  },

  ProductPackage: {
    table: 'product_packages',
    fields: [
      {key: 'packageName', description: 'Package name', wired: false},
      {
        key: 'packageDescription',
        description: 'Package description',
        wired: false,
      },
      {key: 'packageType', description: 'Low, Mid, or High', wired: false},
      {
        key: 'finalMonthlyPrice',
        description: 'Final monthly price',
        wired: false,
      },
    ],
  },

  Product: {
    table: 'products',
    fields: [
      {key: 'name', description: 'Product name', wired: false},
      {key: 'description', description: 'Product description', wired: false},
      {key: 'slug', description: 'URL slug for the product', wired: false},
    ],
  },

  Plan: {
    table: 'plans',
    fields: [
      {
        key: 'name',
        description: 'Plan name, e.g. "Low", "Mid", "High" or a custom name',
        wired: true,
      },
      {key: 'description', description: 'Plan description', wired: true},
      {key: 'tier', description: 'low, mid, or high', wired: false},
    ],
  },

  PromoCode: {
    table: 'promo_codes',
    fields: [
      {
        key: 'code',
        description: 'The promo code text itself, e.g. GREEN10',
        wired: false,
      },
      {
        key: 'description',
        description: "Description of the promo code's offer",
        wired: false,
      },
      {
        key: 'discountValue',
        description: 'Discount value (percentage or flat amount)',
        wired: false,
      },
      {key: 'discountType', description: 'percentage or flat', wired: false},
      {
        key: 'maximumDiscountAmount',
        description: 'Maximum discount amount this code can apply',
        wired: false,
      },
      {
        key: 'expirationDate',
        description: 'Date the promo code expires',
        wired: false,
      },
    ],
  },

  ContactInquiry: {
    table: 'contact_inquiries',
    fields: [
      {
        key: 'inquiryId',
        description: 'Contact form inquiry ID (DB field: id)',
        wired: true,
      },
      {
        key: 'message',
        description: 'The message the visitor submitted',
        wired: true,
      },
      {key: 'name', description: 'Name the visitor submitted', wired: false},
      {key: 'email', description: 'Email the visitor submitted', wired: false},
      {
        key: 'phone',
        description: 'Phone number the visitor submitted',
        wired: false,
      },
      {
        key: 'status',
        description: 'pending, read, replied, or archived',
        wired: false,
      },
    ],
  },

  NewsletterSubscriber: {
    table: 'newsletter_subscribers',
    fields: [
      {key: 'email', description: "Subscriber's email address", wired: true},
      {key: 'status', description: 'active or unsubscribed', wired: false},
    ],
  },

  BillingAddress: {
    table: 'billing_addresses',
    fields: [
      {key: 'country', description: "User's billing country", wired: false},
      {
        key: 'countryCode',
        description: "User's billing country code",
        wired: false,
      },
    ],
  },
};
